import React from 'react';

/**
 * App-wide error boundary.
 *
 * Purpose: prevents a white-screen when any routed page crashes during
 * render. Without this, an uncaught throw in a leaf component unmounts the
 * whole tree and the user sees a completely blank page with no way to
 * recover (except a manual refresh). This boundary catches the error,
 * shows a visible message + reload button, AND surfaces the stack + a
 * component stack so we can diagnose production-only crashes that the dev
 * server masks (React 19 minified builds throw hard where dev only warns).
 *
 * Intentional design choices:
 *   - Uses `componentDidCatch` to log the error to console AND POST to
 *     `/api/client-errors` (best-effort — never re-throws) so future
 *     crashes generate a server-side log without needing the user to open
 *     DevTools.
 *   - `resetKey` (typically the current pathname passed by the parent)
 *     auto-resets the boundary on navigation, so a broken page doesn't
 *     poison the rest of the app.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught error in render tree:', error, info);
    // Best-effort: ship the error to the backend so we can see it in server logs.
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      if (backend && typeof fetch === 'function') {
        fetch(`${backend}/api/client-errors`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
          body: JSON.stringify({
            message: String(error?.message || error || 'unknown'),
            stack: String(error?.stack || ''),
            component_stack: String(info?.componentStack || ''),
            path: typeof window !== 'undefined' ? window.location.pathname : '',
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          }),
        }).catch(() => {});
      }
    } catch (_) { /* swallow — never let the boundary itself crash */ }
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, info: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const message = String(this.state.error?.message || this.state.error || 'Unknown error');
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="error-boundary">
        <div className="max-w-2xl w-full bg-white border border-red-200 rounded-xl shadow-sm">
          <div className="px-5 py-4 border-b border-red-100 bg-red-50/60 rounded-t-xl">
            <h2 className="text-lg font-bold text-red-800" style={{ fontFamily: 'Outfit' }}>
              Something went wrong on this page
            </h2>
            <p className="text-xs text-red-700/80 mt-1">
              The page crashed while rendering. You can safely reload or go back — the rest of the app still works.
            </p>
          </div>
          <div className="p-5 space-y-3">
            <div className="text-xs font-mono text-[#3B5A54] break-words bg-[#F4F9F7] rounded p-3" data-testid="error-message">
              {message}
            </div>
            {process.env.NODE_ENV !== 'production' && this.state.info?.componentStack && (
              <details className="text-[11px] text-[#6D8E87]">
                <summary className="cursor-pointer">Component stack</summary>
                <pre className="mt-2 whitespace-pre-wrap font-mono bg-[#F4F9F7] p-3 rounded max-h-64 overflow-auto">
                  {this.state.info.componentStack}
                </pre>
              </details>
            )}
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-lg bg-[#00a69c] text-white text-sm font-semibold hover:bg-[#00bfb3]"
                data-testid="error-reload-btn"
              >
                Reload page
              </button>
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 rounded-lg border border-[#D5E8E4] text-[#3B5A54] text-sm font-semibold hover:bg-[#F4F9F7]"
                data-testid="error-back-btn"
              >
                Go back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
